// src/lib/metrics/regression.ts
/**
 * Regression metrics - alpha, beta, correlation rolling calculations
 */

import type { Series, BenchmarkMetrics } from './types';
import { TRADING_DAYS } from './types';
import { filterValid, mean, covariance, variance, correlation } from './primitives';
import { upsideCapture, downsideCapture, captureSpread } from './ratios';

// ============================================================================
// ALPHA & BETA (CAPM)
// ============================================================================

/**
 * Calculate beta (systematic risk measure)
 * Beta = Cov(asset, benchmark) / Var(benchmark)
 * 
 * @param returns - Asset returns
 * @param benchmarkReturns - Benchmark returns
 * @returns Beta coefficient
 */
export function computeBeta(
  returns: Series,
  benchmarkReturns: Series
): number | null {
  const cov = covariance(returns, benchmarkReturns);
  const benchVar = variance(benchmarkReturns);
  
  if (cov === null || benchVar === null || benchVar === 0) {
    return null;
  }
  
  return cov / benchVar;
}

/**
 * Calculate alpha (excess return over CAPM prediction)
 * Alpha = Mean(asset) - Beta * Mean(benchmark)
 * 
 * @param returns - Asset returns
 * @param benchmarkReturns - Benchmark returns
 * @param annualize - Whether to annualize alpha
 * @param tradingDays - Trading days for annualization
 * @returns Annualized alpha
 */
export function computeAlpha(
  returns: Series,
  benchmarkReturns: Series,
  annualize: boolean = true,
  tradingDays: number = TRADING_DAYS.CRYPTO
): number | null {
  const beta = computeBeta(returns, benchmarkReturns);
  if (beta === null) return null;
  
  const assetMean = mean(returns);
  const benchMean = mean(benchmarkReturns);
  
  if (assetMean === null || benchMean === null) return null;
  
  const dailyAlpha = assetMean - beta * benchMean;
  
  if (annualize) {
    return dailyAlpha * tradingDays;
  }
  
  return dailyAlpha;
}

/**
 * Calculate both alpha and beta in one call
 */
export function computeAlphaBeta(
  returns: Series,
  benchmarkReturns: Series,
  annualize: boolean = true,
  tradingDays: number = TRADING_DAYS.CRYPTO
): { alpha: number | null; beta: number | null } {
  const beta = computeBeta(returns, benchmarkReturns);
  const alpha = computeAlpha(returns, benchmarkReturns, annualize, tradingDays);
  
  return { alpha, beta };
}

/**
 * Calculate downside beta
 * Beta calculated only on days when benchmark is negative
 */
export function computeDownsideBeta(
  returns: Series,
  benchmarkReturns: Series
): number | null {
  const valid = filterValid(returns);
  const validBench = filterValid(benchmarkReturns);
  
  const n = Math.min(valid.length, validBench.length);
  if (n < 5) return null;
  
  const r = valid.slice(-n);
  const b = validBench.slice(-n);
  
  // Filter for negative benchmark days
  const downDayAsset: number[] = [];
  const downDayBench: number[] = [];
  
  for (let i = 0; i < n; i++) {
    if (b[i]! < 0) {
      downDayAsset.push(r[i]!);
      downDayBench.push(b[i]!);
    }
  }
  
  if (downDayAsset.length < 2) return null;
  
  return computeBeta(downDayAsset, downDayBench);
}

/**
 * Calculate upside beta
 * Beta calculated only on days when benchmark is positive
 */
export function computeUpsideBeta(
  returns: Series,
  benchmarkReturns: Series
): number | null {
  const valid = filterValid(returns);
  const validBench = filterValid(benchmarkReturns);
  
  const n = Math.min(valid.length, validBench.length);
  if (n < 5) return null;
  
  const r = valid.slice(-n);
  const b = validBench.slice(-n);
  
  // Filter for positive benchmark days
  const upDayAsset: number[] = [];
  const upDayBench: number[] = [];
  
  for (let i = 0; i < n; i++) {
    if (b[i]! > 0) {
      upDayAsset.push(r[i]!);
      upDayBench.push(b[i]!);
    }
  }
  
  if (upDayAsset.length < 2) return null;
  
  return computeBeta(upDayAsset, upDayBench);
}

// ============================================================================
// ALL BENCHMARK METRICS
// ============================================================================

/**
 * Calculate all benchmark-relative metrics in one call
 */
export function computeBenchmarkMetrics(
  returns: Series,
  benchmarkReturns: Series,
  annualize: boolean = true,
  tradingDays: number = TRADING_DAYS.CRYPTO
): BenchmarkMetrics {
  return {
    alpha: computeAlpha(returns, benchmarkReturns, annualize, tradingDays),
    beta: computeBeta(returns, benchmarkReturns),
    correlation: correlation(returns, benchmarkReturns),
    upsideCapture: upsideCapture(returns, benchmarkReturns),
    downsideCapture: downsideCapture(returns, benchmarkReturns),
    captureSpread: captureSpread(returns, benchmarkReturns),
  };
}

// ============================================================================
// ROLLING REGRESSION
// ============================================================================

/**
 * Calculate rolling beta
 * 
 * @param returns - Asset returns
 * @param benchmarkReturns - Benchmark returns
 * @param window - Rolling window size
 * @param minPoints - Minimum points for valid calculation
 */
export function rollingBeta(
  returns: Series,
  benchmarkReturns: Series,
  window: number,
  minPoints?: number
): number[] {
  const valid = filterValid(returns);
  const validBench = filterValid(benchmarkReturns);
  
  const n = Math.min(valid.length, validBench.length);
  const r = valid.slice(-n);
  const b = validBench.slice(-n);
  
  const minPts = minPoints ?? Math.max(10, Math.floor(window * 0.4));
  const result: number[] = [];
  
  for (let i = 0; i < n; i++) {
    const start = Math.max(0, i - window + 1);
    const windowR = r.slice(start, i + 1);
    const windowB = b.slice(start, i + 1);
    
    if (windowR.length >= minPts) {
      const beta = computeBeta(windowR, windowB);
      result.push(beta ?? NaN);
    } else {
      result.push(NaN);
    }
  }
  
  return result;
}

/**
 * Calculate rolling correlation
 * 
 * @param returns - Asset returns
 * @param benchmarkReturns - Benchmark returns
 * @param window - Rolling window size
 * @param minPoints - Minimum points for valid calculation
 */
export function rollingCorrelation(
  returns: Series,
  benchmarkReturns: Series,
  window: number,
  minPoints?: number
): number[] {
  const valid = filterValid(returns);
  const validBench = filterValid(benchmarkReturns);
  
  const n = Math.min(valid.length, validBench.length);
  const r = valid.slice(-n);
  const b = validBench.slice(-n);
  
  const minPts = minPoints ?? Math.max(10, Math.floor(window * 0.4));
  const result: number[] = [];
  
  for (let i = 0; i < n; i++) {
    const start = Math.max(0, i - window + 1);
    const windowR = r.slice(start, i + 1);
    const windowB = b.slice(start, i + 1);
    
    if (windowR.length >= minPts) {
      const corr = correlation(windowR, windowB);
      result.push(corr ?? NaN);
    } else {
      result.push(NaN);
    }
  }
  
  return result;
}

/**
 * Calculate rolling alpha
 */
export function rollingAlpha(
  returns: Series,
  benchmarkReturns: Series,
  window: number,
  minPoints?: number,
  annualize: boolean = true,
  tradingDays: number = TRADING_DAYS.CRYPTO
): number[] {
  const valid = filterValid(returns);
  const validBench = filterValid(benchmarkReturns);
  
  const n = Math.min(valid.length, validBench.length);
  const r = valid.slice(-n);
  const b = validBench.slice(-n);
  
  const minPts = minPoints ?? Math.max(10, Math.floor(window * 0.4));
  const result: number[] = [];
  
  for (let i = 0; i < n; i++) {
    const start = Math.max(0, i - window + 1);
    const windowR = r.slice(start, i + 1);
    const windowB = b.slice(start, i + 1);
    
    if (windowR.length >= minPts) {
      const alpha = computeAlpha(windowR, windowB, annualize, tradingDays);
      result.push(alpha ?? NaN);
    } else {
      result.push(NaN);
    }
  }
  
  return result;
}

// ============================================================================
// CORRELATION STABILITY
// ============================================================================

/**
 * Calculate correlation stability
 * Standard deviation of rolling correlations
 * 
 * Low values indicate stable correlation with benchmark
 */
export function correlationStability(
  returns: Series,
  benchmarkReturns: Series,
  rollingWindow: number = 30
): number | null {
  const rollingCorrs = rollingCorrelation(returns, benchmarkReturns, rollingWindow);
  
  const valid = rollingCorrs.filter(c => Number.isFinite(c));
  if (valid.length < 10) return null;
  
  const m = valid.reduce((a, b) => a + b, 0) / valid.length;
  const variance = valid.reduce((acc, c) => acc + Math.pow(c - m, 2), 0) / (valid.length - 1);
  
  return Math.sqrt(variance);
}

